// Sección "Gancho" que se inyecta en el `ConfigurePanel` cuando el modo es
// "Video con influencer" (`showGancho`). Es una opción ADICIONAL dentro de la
// pantalla de configurar plantilla-video (que ya trae productos, modelo,
// formato, "Configurar video", etc.) — acá solo va el selector de gancho.
//
// Render UNIFICADO mobile/desktop: una fila que abre un panel de selección (el
// `GanchoPicker` desktop es responsive → bottom sheet en mobile, panel lateral
// en desktop). Debajo, el link "¿Qué es un gancho?" abre el explicador. NO
// lleva título "Gancho" propio: en desktop ya está la sección "Planeamiento
// del video"; en mobile va suelto.
//
// SCAFFOLD: los ganchos son placeholders (la tabla no existe todavía).

import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { GanchoPicker } from './GanchoPicker';
import { GanchoExplainerSheet } from './GanchoExplainerSheet';
import { fetchVideoHooks, type GanchoOption } from '../ganchosCatalog';

export interface GanchoSectionProps {
  /** Clase del wrapper. Default `mt-5` (uso suelto, ej. mobile); pasá `''`
   *  cuando va dentro de un contenedor con `gap`/`space-y` propio (ej. la
   *  sección "Planeamiento del video" del desktop). */
  className?: string;
  /** Id del gancho elegido. Si se pasan `selectedGanchoId` + `onSelectGancho`
   *  el componente es CONTROLADO (la selección vive arriba, ej. InterfazV2, que
   *  la manda como `gancho_id` en la generación de video-influencer). Si no, cae
   *  a estado interno (retrocompat). */
  selectedGanchoId?: string | null;
  onSelectGancho?: (id: string | null) => void;
}

export const GanchoSection: React.FC<GanchoSectionProps> = ({
  className = 'mt-5',
  selectedGanchoId,
  onSelectGancho,
}) => {
  const [ganchos, setGanchos] = useState<GanchoOption[]>([]);
  // Controlado si el padre pasó el callback; si no, estado interno.
  const controlled = onSelectGancho !== undefined;
  const [internalSel, setInternalSel] = useState<string | null>(null);
  const selectedGancho = controlled ? selectedGanchoId ?? null : internalSel;
  const setSelectedGancho = (id: string | null) =>
    controlled ? onSelectGancho!(id) : setInternalSel(id);
  const [ganchoPanelOpen, setGanchoPanelOpen] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  // Fetch de los ganchos (`video_hooks`) al montar. Si no hay filas, el
  // picker cae a slots placeholder.
  useEffect(() => {
    let alive = true;
    void fetchVideoHooks().then((rows) => {
      if (alive) setGanchos(rows);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Mismo render en mobile y desktop: la fila abre el panel de selección. El
  // `GanchoPicker` desktop es responsive (bottom sheet en mobile).
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setGanchoPanelOpen(true)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
      >
        <span className="text-[13.5px] text-[#3C3C43]/70">Elegí un gancho</span>
        <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-black/85">
          {selectedGancho ? 'Seleccionado' : 'Elegir'}
          <ChevronRight className="w-3.5 h-3.5 text-[#3C3C43]/40" />
        </span>
      </button>

      {/* "¿Qué es un gancho?" — link al explicador */}
      <div className="mt-2.5">
        <button
          type="button"
          onClick={() => setExplainerOpen(true)}
          className="text-[12.5px] text-[#3C3C43]/45 hover:text-brand-primary underline-offset-2 hover:underline transition-colors"
        >
          ¿Qué es un gancho?
        </button>
      </div>

      {/* Panel de selección — bottom sheet en mobile, lateral en desktop */}
      <GanchoPicker
        variant="desktop"
        ganchos={ganchos}
        open={ganchoPanelOpen}
        onClose={() => setGanchoPanelOpen(false)}
        selectedId={selectedGancho}
        onSelect={(id) => {
          setSelectedGancho(id);
          setGanchoPanelOpen(false);
        }}
      />

      {/* Explicador "¿Qué es un gancho?" */}
      <GanchoExplainerSheet
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
      />
    </div>
  );
};
